import React, { useEffect } from 'react';
import { Alert, AppState, Linking, Platform } from 'react-native';
import { productProfile } from '../productProfile';
import {
  doesPublicApkChecksumMatch,
  isPublicApkUpdateAvailable,
  parsePublicApkRelease,
  publicApkChecksumUrl,
} from '../utils/publicApkUpdate';

const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

export function PublicApkUpdateMonitor() {
  useEffect(() => {
    if (
      Platform.OS !== 'android'
      || productProfile.kind !== 'public'
      || !productProfile.androidUpdateManifestUrl
      || productProfile.appVersionCode <= 0
    ) return undefined;

    let stopped = false;
    let checking = false;
    let lastCheckAt = 0;
    let announcedVersionCode = 0;

    const check = async (force = false) => {
      const now = Date.now();
      if (stopped || checking || (!force && now - lastCheckAt < CHECK_INTERVAL_MS)) return;
      checking = true;
      lastCheckAt = now;
      try {
        const response = await fetch(productProfile.androidUpdateManifestUrl, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const release = parsePublicApkRelease(await response.json());
        if (!release || !isPublicApkUpdateAvailable(productProfile.appVersionCode, release)) return;
        const checksumResponse = await fetch(publicApkChecksumUrl(release), { cache: 'no-store' });
        if (!checksumResponse.ok || !doesPublicApkChecksumMatch(release, await checksumResponse.text())) return;
        if (release.versionCode === announcedVersionCode) return;
        announcedVersionCode = release.versionCode;
        const hashPreview = `${release.sha256.slice(0, 12)}…${release.sha256.slice(-8)}`;
        Alert.alert(
          'Jellyflix update available',
          `Version ${release.versionName} is ready.\n\nSHA-256: ${hashPreview}`,
          [
            { text: 'Later', style: 'cancel' },
            {
              text: 'Download update',
              onPress: () => { void Linking.openURL(release.url); },
            },
          ],
        );
      } catch {
        // Update discovery must never delay launch or take the client offline.
      } finally {
        checking = false;
      }
    };

    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') void check();
    });
    void check(true);
    return () => {
      stopped = true;
      subscription.remove();
    };
  }, []);

  return null;
}
